import { AxiosResponse } from "axios";

import apiClient from "../../../../shared/infrastructure/api/client";
import { BillingPeriod, CancellationReason, CouponDTO, PlanTier } from "../../domain/types";
import { CenterSubscriptionStateDTO } from "../../domain/centerTypes";

/**
 * A centre's seats. Every route is behind the centre's administrator check on
 * the server: a therapist's plan comes from the seat, and the centre's
 * finances are not theirs to read.
 */
export const CenterSubscriptionAPI = {
    state: (centerId: string): Promise<AxiosResponse<CenterSubscriptionStateDTO>> =>
        apiClient.get(`/centers/${centerId}/subscription`),

    /**
     * Buy seats. `seats` is sent every time and has no default on the server:
     * the number a centre was created with is not a purchase anybody agreed
     * to, so the administrator says how many they are buying.
     */
    checkout: (
        centerId: string,
        period: BillingPeriod,
        seats: number,
        coupon?: string,
        modules: string[] = [],
        tier: PlanTier = "plus"
    ): Promise<AxiosResponse<{ url: string }>> =>
        apiClient.post(`/centers/${centerId}/subscription/checkout`, {
            period,
            seats,
            coupon,
            modules,
            tier,
        }),

    /** Move every seat between Plus and Max. */
    changeTier: (
        centerId: string,
        tier: PlanTier
    ): Promise<AxiosResponse<CenterSubscriptionStateDTO>> =>
        apiClient.post(`/centers/${centerId}/subscription/tier`, { tier }),

    /** What a discount code is worth, before the centre pays. */
    coupon: (centerId: string, code: string): Promise<AxiosResponse<CouponDTO>> =>
        apiClient.post(`/centers/${centerId}/subscription/coupon`, { code }),

    /** The only call here that moves money. The provider prorates it. */
    setSeats: (
        centerId: string,
        seats: number
    ): Promise<AxiosResponse<CenterSubscriptionStateDTO>> =>
        apiClient.put(`/centers/${centerId}/subscription/seats`, { seats }),

    cancel: (
        centerId: string,
        reason?: CancellationReason,
        comment?: string
    ): Promise<AxiosResponse<CenterSubscriptionStateDTO>> =>
        apiClient.post(`/centers/${centerId}/subscription/cancel`, {
            reason: reason ?? null,
            comment: comment?.trim() ? comment.trim() : null,
        }),

    resume: (centerId: string): Promise<AxiosResponse<CenterSubscriptionStateDTO>> =>
        apiClient.post(`/centers/${centerId}/subscription/resume`),

    /** With `updatePaymentMethod`, the portal opens straight on changing the card. */
    billingPortal: (
        centerId: string,
        updatePaymentMethod = false
    ): Promise<AxiosResponse<{ url: string }>> =>
        apiClient.post(`/centers/${centerId}/subscription/billing-portal`, {
            update_payment_method: updatePaymentMethod,
        }),
};
